/**
 * Tote bag painting — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_paint_crafts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const toteBagPaintingPack: ToilePack = {
    slug: 'tote-bag-painting',
    category: 'craft',
    name: 'Tote bag painting',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'floral', label: 'Floral', subject: 'a canvas tote bag painted with wildflowers'},
        {key: 'quote', label: 'Quote', subject: 'a tote bag painted with the words "Good Vibes"'},
        {key: 'character', label: 'Character', subject: 'a tote bag painted with a cartoon cat'},
    ],
    styles: [
        {key: 'watercolor', label: 'Watercolor', style: 'soft fabric paint washes with layered translucent color', palette: 'muted greens, blush and lilac on natural cream canvas', framing: 'hanging from a wooden hook on a white wall, the bag flat and front-facing'},
        {key: 'bold', label: 'Bold acrylic', style: 'opaque fabric acrylic in flat shapes with crisp black outlines', palette: 'bright orange, cobalt and lemon on natural canvas', framing: 'laid flat on a wooden floor, seen from directly above, the handles fanned out'},
        {key: 'ink', label: 'Black ink', style: 'a black fabric marker drawing with fine hatching', palette: 'black ink only on natural canvas', framing: 'propped on a chair beside a potted plant, a slight three-quarter view'},
    ],
    directions: ['beach bag', 'sunflowers', 'for teacher'],
    directionHint: 'try: beach bag',
    seo: {
        title: 'Tote bag painting: floral, quote, character',
        metaDescription: 'Nine tote bag painting ideas, a floral, a quote and a cartoon cat bag, in watercolour, bold acrylic and black ink.',
        h1: 'Tote bag painting ideas: floral, quote, cartoon character',
        tagline: 'Three canvas totes, each in watercolour washes, bold acrylic or black ink.',
        intro: 'A canvas tote is a simple base for fabric paint. These nine show three designs, a tote with wildflowers, a tote with the words Good Vibes and a tote with a cartoon cat, each in one of three finishes: soft watercolour washes, bold acrylic with black outlines, or black ink with fine hatching. The bags are shown hung on a wall, laid flat, or propped on a chair. Type a subject, such as beach bag, to ask for it.',
        useCases: [
            'an everyday shopping bag',
            'a beach or market bag',
            'a gift bag for a teacher',
            'a bag for a school or class project',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'A wildflower tote, a Good Vibes quote tote and a cartoon cat tote, each in watercolour, bold acrylic and black ink.'},
            {q: 'Are these photos of real bags?', a: 'No. They are generated pictures of each design, not photographs of bags someone painted.'},
            {q: 'What is the difference between the finishes?', a: 'Watercolour uses soft, layered washes. Bold acrylic uses flat, opaque shapes with black outlines. Black ink is a fine marker drawing with hatching.'},
            {q: 'Can I ask for a different design?', a: 'Yes. Type a subject such as sunflowers, a beach bag or a teacher gift. A typed subject is a request, not a promised result.'},
        ],
    },
}
